import React, { FC, useState } from 'react';
import moment from 'moment';
import { Popover, Tag } from 'antd';

// Colours/labels for each break reason. The backend sends green (work zone),
// yellow (non-work zone), and gray (no zone); previously only gray/green were
// handled, so yellow periods fell through to the grey track.
const BREAK_PALETTE: Record<string, { bar: string; tag: string; tagText: string; label: string }> = {
  green: { bar: '#4caf50', tag: '#4caf50', tagText: 'white', label: 'Рабочее время' },
  yellow: { bar: '#fdf39b', tag: '#fae739', tagText: 'black', label: 'Перерыв' },
  gray: { bar: '#bdbdbd', tag: '#bfbfbf', tagText: 'black', label: 'Нет зоны' }
};

interface IBreak {
  start: number; // Время начала перерыва в миллисекундах
  end: number; // Время окончания перерыва в миллисекундах
  reason: string; // Причина перерыва
  areaName?: string;
  tempMinutes: number;
}

interface ISchedule {
  workStart: number; // Ожидаемое время начала работы в миллисекундах
  actualStart: number; // Фактическое время начала работы в миллисекундах
  workEnd: number; // Ожидаемое время окончания работы в миллисекундах
  actualEnd: number; // Фактическое время окончания работы в миллисекундах
  breaks: IBreak[];
}

interface IProps {
  graphData: ISchedule;
}

const WorkDayProgressBar: React.FC<{ schedule: ISchedule }> = ({ schedule }) => {
  const { workStart, actualStart, workEnd, actualEnd, breaks } = schedule;

  // Guard against zero/negative/NaN (degenerate or malformed shifts) so the
  // percentages below don't divide by 0 or go negative.
  const totalWorkTime = workEnd > workStart ? workEnd - workStart : 1;

  // Фактическое рабочее время с учетом перерывов
  const actualWorkTimeWithBreaks = Math.max(0, actualEnd - actualStart);

  // Процент выполненной работы
  const progressPercentage = (actualWorkTimeWithBreaks / totalWorkTime) * 100;

  // Опоздание и ранний уход
  const delayInMillis = Math.max(0, actualStart - workStart);
  const earlyLeaveInMillis = Math.max(0, workEnd - actualEnd);

  // Процент опоздания и раннего ухода
  const delayPercentage = (delayInMillis / totalWorkTime) * 100;
  const earlyLeavePercentage = (earlyLeaveInMillis / totalWorkTime) * 100;

  interface IShiftProps {
    startTimeMs: number; // время начала в миллисекундах
    endTimeMs: number; // время конца в миллисекундах
  }

  const formatTime = (date: Date): string => {
    const hours = date
      .getHours()
      .toString()
      .padStart(2, '0');
    const minutes = date
      .getMinutes()
      .toString()
      .padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const generateHourlyMarks = (start: Date, end: Date): JSX.Element[] => {
    const result: JSX.Element[] = [];

    // Находим первый час кратный 3, который больше или равен start
    const startHour = start.getHours();
    let firstMarkHour = startHour - (startHour % 3);
    if (firstMarkHour < startHour) {
      firstMarkHour += 3; // следующий кратный 3 час после start
    }

    // Создаем дату для первого маркера
    const currentMark = new Date(start);

    // Устанавливаем час для первого маркера
    currentMark.setHours(firstMarkHour, 0, 0, 0);

    while (currentMark <= end) {
      // Добавляем только если текущий час кратен трем и не совпадает с началом или концом
      if (
        currentMark >= start &&
        currentMark <= end &&
        currentMark.getHours() % 3 === 0 &&
        !(currentMark.getTime() === start.getTime()) && // исключаем точное совпадение с началом
        !(currentMark.getTime() === end.getTime()) // исключаем точное совпадение с концом
      ) {
        result.push(
          <span key={currentMark.getTime()} style={{ margin: '0 4px' }}>
            {formatTime(currentMark)}
          </span>
        );
      }
      currentMark.setHours(currentMark.getHours() + 3);
    }

    return result;
  };

  const WorkShiftTimeline: React.FC<IShiftProps> = ({ startTimeMs, endTimeMs }) => {
    const startDate = new Date(startTimeMs);
    const endDate = new Date(endTimeMs);

    // Обработка случая если время конца раньше времени начала
    if (endDate <= startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }

    const hourlyMarks = generateHourlyMarks(startDate, endDate);

    return (
      <>
        {/* Время начала смены */}
        <span style={{ fontWeight: 'bold', marginRight: '8px' }}>{formatTime(startDate)}</span>

        {/* Внутренние часы кратные трем */}
        {hourlyMarks}

        {/* Время конца смены */}
        <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>{formatTime(endDate)}</span>
      </>
    );
  };

  // Функция для генерации линий часов
  const generateHourlyLines = (startTimestamp: number, endTimestamp: number): JSX.Element[] => {
    const lines: JSX.Element[] = [];

    const start = new Date(startTimestamp);
    const end = new Date(endTimestamp);

    // Начинаем с ближайшего часа после start
    start.setMinutes(0, 0, 0);
    if (start.getTime() < startTimestamp) {
      start.setHours(start.getHours() + 1);
    }

    while (start <= end) {
      const hour = start.getHours();
      if (hour % 3 === 0) {
        lines.push(
          <div
            key={start.getTime()}
            style={{
              flex: '1',
              borderLeft: '1px dashed #64727c',
              height: '6px',
              position: 'relative'
            }}
            title={formatTime(start)} // функция для форматирования времени
          />
        );
      }
      start.setHours(start.getHours() + 1);
    }

    return lines;
  };

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '550px',
        margin: '2px auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center' /* Центрируем по горизонтали */,
        justifyContent: 'center',
        position: 'relative'
      }}
    >
      <div
        style={{
          position: 'relative',
          height: '25px',
          width: '90%',
          maxWidth: '500px',
          margin: '5px 0',
          // borderRadius: '15px',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            position: 'relative',
            height: '20px',
            backgroundColor: '#eee',
            // borderRadius: '15px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: `0%`,
              width: `${delayPercentage}%`,
              height: '100%',
              backgroundColor: 'rgb(238, 238, 238)',
              pointerEvents: 'none'
            }}
          />
          {/*<Popover*/}
          {/*  key={'delayPercentage'}*/}
          {/*  placement="bottom"*/}
          {/*  title={<Tag color="#4caf50">Рабочее время</Tag>}*/}
          {/*  content={*/}
          {/*    <div>*/}
          {/*      <p style={{ fontWeight: 'bold' }}>*/}
          {/*        {moment(actualStart).format('HH:mm')} - {moment(actualEnd).format('HH:mm')}*/}
          {/*      </p>*/}
          {/*      <p>{moment(actualStart).format('DD.MM.YYYY')}</p>*/}
          {/*    </div>*/}
          {/*  }*/}
          {/*>*/}
          {/*  <div*/}
          {/*    style={{*/}
          {/*      position: 'absolute',*/}
          {/*      height: '100%',*/}
          {/*      left: `${delayPercentage}%`,*/}
          {/*      width: `${progressPercentage}%`,*/}
          {/*      backgroundColor: '#4caf50',*/}
          {/*      transition: 'width .5s ease'*/}
          {/*    }}*/}
          {/*  />*/}
          {/*</Popover>*/}
          {breaks.map((b, index) => {
            const palette = BREAK_PALETTE[b.reason] ?? BREAK_PALETTE.gray;
            const startMoment = moment(b.start);
            const endMoment = moment(b.end);

            return (
              <Popover
                key={index}
                placement="bottom"
                title={
                  <Tag color={palette.tag} style={{ color: palette.tagText }}>
                    {palette.label}
                  </Tag>
                }
                content={
                  <div>
                    <p style={{ fontWeight: 'bold' }}>
                      {startMoment.format('HH:mm')} - {endMoment.format('HH:mm')}
                    </p>
                    <p>{startMoment.format('DD.MM.YYYY')}</p>
                    {b.areaName && <p>{b.areaName}</p>}
                  </div>
                }
              >
                <div
                  style={{
                    position: 'absolute',
                    left: `${((b.start - workStart) / totalWorkTime) * 100}%`,
                    width: `${((b.end - b.start) / totalWorkTime) * 100}%`,
                    top: 0,
                    bottom: 0,
                    backgroundColor: palette.bar,
                    pointerEvents: 'auto'
                  }}
                />
              </Popover>
            );
          })}

          <Popover
            key={'delayPercentage'}
            placement="bottom"
            title={<Tag color="#4caf50">Начало работы</Tag>}
            content={
              <div>
                <p style={{ fontWeight: 'bold' }}>{moment(actualStart).format('HH:mm')}</p>
                <p>{moment(actualStart).format('DD.MM.YYYY')}</p>
              </div>
            }
          >
            <div
              style={{
                position: 'absolute',
                left: `${delayPercentage}%`,
                width: `5px`,
                height: '100%',
                backgroundColor: '#616b76',
                pointerEvents: 'auto'
              }}
            />
          </Popover>

          <Popover
            // key={'delayPercentage'}
            placement="bottom"
            title={<Tag color="#4caf50">Завершение работы</Tag>}
            content={
              <div>
                <p style={{ fontWeight: 'bold' }}>{moment(actualEnd).format('HH:mm')}</p>
                <p>{moment(actualEnd).format('DD.MM.YYYY')}</p>
              </div>
            }
          >
            <div
              style={{
                position: 'absolute',
                left: `${delayPercentage + progressPercentage}%`,
                width: `5px`,
                height: '100%',
                top: 0,
                bottom: 0,
                backgroundColor: '#616b76',
                pointerEvents: 'auto'
              }}
            />
          </Popover>
        </div>
        <div style={{ marginTop: '-3px' }}>
          {/* Линии часов */}
          <div
            style={{
              display: 'flex',
              position: 'absolute',
              width: '100%',
              height: '2px',
              marginTop: '2px',
              backgroundColor: '#2886ae'
            }}
          />
          <div style={{ display: 'flex', width: '100%' }}>
            {generateHourlyLines(workStart, workEnd)}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative',
          width: '100%',
          fontSize: '14px',
          color: '#555'
        }}
      >
        <WorkShiftTimeline startTimeMs={schedule.workStart} endTimeMs={schedule.workEnd} />
      </div>
    </div>
  );
};

// Функция для парсинга данных из бэкенда
const parseScheduleData = (data: {
  workStart: any;
  actualStart: any;
  workEnd: any;
  actualEnd: any;
  breaks: any;
}): ISchedule => {
  return {
    workStart: new Date(data.workStart).getTime(),
    actualStart: new Date(data.actualStart).getTime(),
    workEnd: new Date(data.workEnd).getTime(),
    actualEnd: new Date(data.actualEnd).getTime(),
    breaks: data.breaks.map(
      (b: { start: string | number | Date; end: string | number | Date; reason: any; areaName?: string }) => ({
        start: new Date(b.start).getTime(),
        end: new Date(b.end).getTime(),
        reason: b.reason,
        areaName: b.areaName
      })
    )
  };
};

const Chart: FC<IProps> = ({ graphData }) => {
  // Парсим данные перед передачей их в компонент
  const parsedScheduleData = parseScheduleData(graphData);

  return <WorkDayProgressBar schedule={parsedScheduleData} />;
};

export default Chart;
